import { ChevronRight, CreditCard } from 'lucide-react';
import { DataTable, type Column } from '@app/layouts/DataTable';
import { Badge } from '@shared/ui/Badge';
import { formatPrice } from '@shared/lib/format-price';
import { intervalLabel, type SubscriptionPlan } from '@features/plans/model/plan.types';

interface PlansTableProps {
  plans: SubscriptionPlan[];
  loading?: boolean;
  error?: string | null;
  /** Opens the plan detail dialog. Omitted, rows are not clickable. */
  onOpen?: (plan: SubscriptionPlan) => void;
}

/** The plan catalogue: what is offered, and what each plan is carrying. */
export function PlansTable({ plans, loading = false, error = null, onOpen }: PlansTableProps) {
  const columns: Column<SubscriptionPlan>[] = [
    {
      key: 'name',
      header: 'Plan',
      alwaysVisible: true,
      sortValue: (plan) => plan.name,
      // Laid out like the users table's first column: a leading tile, then the
      // name on one line. The tile is what gives that table its row rhythm, and
      // it fixes the row height so a long name cannot grow it.
      //
      // One line, not two: the marketing badge on its own row doubled every
      // row's height for a phrase that is also in the detail dialog. The name
      // is the flex item that shrinks, so the badge never pushes it out of view.
      render: (plan) => (
        <div className="flex items-center gap-2.5">
          <span
            aria-hidden
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-400"
          >
            <CreditCard className="h-3.5 w-3.5" />
          </span>

          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate font-medium text-slate-900">{plan.name}</span>
            {plan.isPopular && (
              <Badge tone="neutral" className="shrink-0">
                Popular
              </Badge>
            )}
            {plan.badge && (
              <span className="hidden shrink-0 text-xs text-slate-400 lg:inline">{plan.badge}</span>
            )}
          </span>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortValue: (plan) => (plan.isActive ? 'Live' : 'Retired'),
      render: (plan) => (
        <Badge tone={plan.isActive ? 'positive' : 'neutral'}>
          {plan.isActive ? 'Live' : 'Retired'}
        </Badge>
      ),
    },
    {
      key: 'interval',
      header: 'Billing',
      sortValue: (plan) => plan.interval,
      render: (plan) => <span className="whitespace-nowrap text-slate-700">{intervalLabel(plan.interval)}</span>,
    },
    {
      key: 'price',
      header: 'Price',
      align: 'right',
      sortValue: (plan) => plan.price ?? null,
      render: (plan) => {
        const price = formatPrice(plan.price, plan.currency);
        const was = formatPrice(plan.originalPrice, plan.currency);

        return (
          <div className="whitespace-nowrap tabular-nums">
            <span className="text-slate-900">{price ?? '—'}</span>
            {/* Only shown when it is actually a discount, not when the two match. */}
            {was && was !== price && (
              <span className="ml-1.5 text-xs text-slate-400 line-through">{was}</span>
            )}
          </div>
        );
      },
    },
    {
      key: 'activeSubscriberCount',
      header: 'Active',
      title: 'Active subscribers',
      align: 'right',
      sortValue: (plan) => plan.activeSubscriberCount,
      render: (plan) => (
        <span
          className={
            plan.activeSubscriberCount > 0 ? 'tabular-nums text-slate-900' : 'text-slate-300'
          }
        >
          {plan.activeSubscriberCount > 0 ? plan.activeSubscriberCount : '—'}
        </span>
      ),
    },
    {
      key: 'subscriberCount',
      header: 'All time',
      title: 'Subscriptions ever created',
      align: 'right',
      sortValue: (plan) => plan.subscriberCount,
      render: (plan) => (
        <span className={plan.subscriberCount > 0 ? 'tabular-nums text-slate-600' : 'text-slate-300'}>
          {plan.subscriberCount > 0 ? plan.subscriberCount : '—'}
        </span>
      ),
    },
    {
      key: 'monthlyRevenue',
      header: 'MRR',
      title: 'Monthly recurring revenue',
      align: 'right',
      sortValue: (plan) => plan.monthlyRevenue,
      render: (plan) => (
        <span
          className={plan.monthlyRevenue > 0 ? 'tabular-nums text-slate-900' : 'text-slate-300'}
        >
          {plan.monthlyRevenue > 0
            ? (formatPrice(round(plan.monthlyRevenue), plan.currency) ?? '—')
            : '—'}
        </span>
      ),
    },
    {
      key: 'features',
      header: 'Features',
      sortValue: (plan) => plan.features.length,
      render: (plan) =>
        plan.features.length > 0 ? (
          // The list itself lives in the detail dialog: spelling it out here
          // would make every row as tall as its longest plan.
          <span className="whitespace-nowrap text-slate-600">
            {plan.features.length} {plan.features.length === 1 ? 'feature' : 'features'}
          </span>
        ) : (
          <span className="text-slate-300">—</span>
        ),
    },
  ];

  if (onOpen) {
    columns.push({
      key: 'open',
      header: '',
      title: 'Details',
      align: 'right',
      alwaysVisible: true,
      className: 'w-8',
      // The whole row is the target; this is only the affordance that says so.
      render: () => <ChevronRight aria-hidden className="ml-auto h-4 w-4 text-slate-300" />,
    });
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white">
      <DataTable
        columns={columns}
        rows={plans}
        rowKey={(plan) => plan.id}
        isLoading={loading}
        isError={Boolean(error)}
        errorMessage={error ?? undefined}
        loadingMessage="Loading plans…"
        emptyMessage="This project has no subscription plans."
        columnToggle={false}
        dense
        {...(onOpen ? { onRowClick: onOpen } : {})}
      />
    </div>
  );
}

/** MRR is an average once yearly plans are divided down; cents add nothing. */
function round(value: number): number {
  return Math.round(value);
}
